// The three-way calculator as a hook: give it the two assets on the screen and
// it hands back a value and a setter per asset, plus the rate. See
// lib/trade-math.ts for the rules; this file only binds them to React state.
import { useCallback, useMemo, useState } from 'react';
import { EMPTY_MATH, edit, orient, fieldIn, repair, rateWords, ratePlaceholder, preset as presetRule, type Field, type TradeMathState } from '../lib/trade-math';
import { displayAsset } from '../lib/format';

export interface TradeMath {
  /** Amount typed or derived for this asset. */
  amountOf: (asset: string) => string;
  /** The operator typed an amount for this asset. */
  setAmount: (asset: string, value: string) => void;
  rate: string;
  setRate: (value: string) => void;
  /** "AED per USDT" */
  rateWords: string;
  /** "how many AED for 1 USDT" */
  ratePlaceholder: string;
  base: string;
  quote: string;
  /** The box the operator has NOT typed — the one that moves. */
  derived: Field | null;
  /** True when this asset's amount is the one the calculator fills — so the
   *  screen can mark it "auto" and the operator sees which box moves. */
  isDerived: (asset: string) => boolean;
  rateIsDerived: boolean;
  state: TradeMathState;
  /** Prefill (a chain row, a balance): counts as typed first. */
  preset: (field: 'base' | 'quote' | 'rate' | { asset: string }, value: string) => void;
  reset: () => void;
}

export function useTradeMath(assetA: string, assetB: string, initial?: Partial<TradeMathState>): TradeMath {
  // A half pair (one side not picked yet) is not oriented: the asset that is
  // there keeps its own box, and orientation happens when the second arrives.
  const pair = useMemo(() => (assetA && assetB ? orient(assetA, assetB) : { base: assetA, quote: assetB }), [assetA, assetB]);
  const [state, setState] = useState<TradeMathState>(() => {
    const st: TradeMathState = { ...EMPTY_MATH, ...(initial ?? {}) };
    // Values handed in without an ownership list count as typed, base first.
    if (!initial?.owned) st.owned = (['base', 'quote', 'rate'] as Field[]).filter((f) => st[f] !== '').slice(0, 2);
    return st;
  });
  // The previous pair lives in STATE, not a ref: React renders a component
  // twice in StrictMode and a ref mutated in the first pass made the second
  // pass believe nothing had changed, so the dev build never re-seated the
  // amounts while the production build did (critic, 12 Sept 2026). A
  // render-phase setState on state is replayed correctly in both.
  const [seen, setSeen] = useState(pair);
  if (seen.base !== pair.base || seen.quote !== pair.quote) {
    setSeen(pair);
    setState((s) => repair(s, seen, pair));
  }
  const fieldFor = useCallback((asset: string): 'base' | 'quote' => fieldIn(asset, pair), [pair]);
  const amountOf = useCallback((asset: string) => state[fieldFor(asset)], [state, fieldFor]);
  const setAmount = useCallback((asset: string, value: string) => setState((s) => edit(s, fieldFor(asset), value, pair)), [fieldFor, pair]);
  const setRate = useCallback((value: string) => setState((s) => edit(s, 'rate', value, pair)), [pair]);
  const preset = useCallback((field: 'base' | 'quote' | 'rate' | { asset: string }, value: string) => {
    const f: Field = typeof field === 'string' ? field : fieldFor(field.asset);
    setState((s) => presetRule(s, f, value, pair));
  }, [fieldFor, pair]);
  const reset = useCallback(() => setState({ ...EMPTY_MATH }), []);
  const derived = state.owned.length === 2 ? (['base', 'quote', 'rate'] as Field[]).find((f) => !state.owned.includes(f)) ?? null : null;
  const isDerived = useCallback((asset: string) => derived !== null && derived !== 'rate' && fieldFor(asset) === derived, [derived, fieldFor]);
  return {
    amountOf, setAmount, rate: state.rate, setRate, isDerived, rateIsDerived: derived === 'rate',
    rateWords: assetA && assetB ? rateWords(assetA, assetB, displayAsset) : '',
    ratePlaceholder: assetA && assetB ? ratePlaceholder(assetA, assetB, displayAsset) : 'rate',
    base: pair.base, quote: pair.quote, derived, state, preset, reset,
  };
}
